<template>
  <b-modal
    id="filter_modal"
    @show="onShow"
    centered
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    scrollable
    size="lg"
  >
    <div>
      <b-row align-v="center" class="d-flex align-items-center">
        <b-col cols="12" class="mb-2">
          <label class="text-capitalize d-block mb-0">
            {{ $tc("dmp.panel") }}
          </label>
          <b-form-checkbox v-model="autoDetectPanel" class="mb-50" switch>
            <span class="text-capitalize small">{{ $t("dmp.autoDetectPanel") }}</span>
          </b-form-checkbox>
          <v-select
            v-model="panelFilter"
            :options="panelOptions"
            :reduce="(val) => val.value"
            :disabled="autoDetectPanel"
            :close-on-select="false"
            :clear-search-on-select="false"
            :hide-selected="false"
            multiple
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
          >
            <template #option="option">
              <div class="panel-option d-flex align-items-center">
                <span
                  class="custom-option-checkbox mr-50"
                  :class="{ 'custom-option-checkbox-selected': panelFilter.includes(option.value) }"
                >
                  <feather-icon
                    v-if="panelFilter.includes(option.value)"
                    icon="CheckIcon"
                    size="13"
                  />
                </span>
                <span>{{ option.label }}</span>
              </div>
            </template>
          </v-select>
          <small v-if="autoDetectPanel" class="text-muted">
            {{ $t("dmp.autoDetectPanelHint") }}
          </small>
        </b-col>

        <!-- Event: what kind, and whether it's been recovered -->
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $tc("dmp.eventDefinition", 2) }}
          </label>
          <v-select
            v-model="eventDefinitionOptionsFilter"
            :options="eventDefinitionOptions"
            :reduce="(val) => val.value"
            multiple
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $tc("dmp.isRecovered", 2) }}
          </label>
          <v-select
            v-model="recoveredFilter"
            :options="[
              { label: $t('dmp.recovered_yes'), value: true },
              { label: $t('dmp.recovered_no'), value: false },
            ]"
            :multiple="false"
            class="w-100"
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
        </b-col>

        <!-- Device: id and name together -->
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("dmp.deviceId") }}
          </label>
          <b-form-input v-model="deviceIdFilter" />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("dmp.deviceName") }}
          </label>
          <b-form-input v-model="deviceNameFilter" />
        </b-col>

        <!-- Date range: fixed start/end together, rolling window at the end -->
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">{{
            $t("alarm.start_date_filter")
          }}</label>
          <flat-pickr
            v-model="startDatetime"
            class="form-control"
            :disabled="!!dateRangeFilter"
            :config="{
              enableTime: true,
              dateFormat: 'Y-m-d H:i:s',
              time_24hr: true,
            }"
            :value="startDatetime"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">{{
            $t("alarm.end_date_filter")
          }}</label>
          <flat-pickr
            v-model="endDatetime"
            class="form-control"
            :disabled="!!dateRangeFilter"
            :config="{
              enableTime: true,
              dateFormat: 'Y-m-d H:i:s',
              time_24hr: true,
            }"
            :value="endDatetime"
          />
        </b-col>
        <b-col cols="6" class="mb-2">
          <label class="text-capitalize">
            {{ $t("dmp.dateRange") }}
          </label>
          <v-select
            v-model="dateRangeFilter"
            :options="[
              { label: $t('dmp.dateRangeNone'), value: null },
              { label: $t('dmp.dateRangeLast30'), value: 30 },
              { label: $t('dmp.dateRangeLast60'), value: 60 },
            ]"
            :multiple="false"
            :clearable="false"
            class="w-100"
            append-to-body
            :calculate-position="withPopper"
            :reduce="(val) => val.value"
          />
          <small v-if="dateRangeFilter" class="text-muted">
            {{ $t("dmp.dateRangeHint") }}
          </small>
        </b-col>
      </b-row>
    </div>
    <template #modal-footer>
      <b-button
        size="sm"
        variant="secondary"
        class="text-capitalize"
        @click="$bvModal.hide('filter_modal')"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        variant="info"
        size="sm"
        class="text-capitalize"
        @click="applyFilters"
      >
        {{ $t("actions.accept") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormInput,
  BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import popperHelper from "@/libs/popper-helper";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BFormInput,
    BFormCheckbox,
    vSelect,
    flatPickr,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      panelFilter: [],
      autoDetectPanel: false,
      eventDefinitionOptionsFilter: [],
      startDatetime: "",
      endDatetime: "",
      dateRangeFilter: null,
      recoveredFilter: null,
      deviceIdFilter: "",
      deviceNameFilter: "",
    };
  },
  computed: {
    panelOptions() {
      return this.$store.getters["dmp_panels/getSelectedItems"];
    },
    eventDefinitionOptions() {
      return this.$store.getters["dmp_events_definition/getSelectedItems"];
    },
    filtersText() {
      const parts = [];
      if (this.autoDetectPanel) {
        parts.push(this.$t("dmp.autoDetectPanel"));
      } else if (this.panelFilter?.length){
        parts.push(this.$tc("dmp.panel"));
      }
      if (this.eventDefinitionOptionsFilter?.length){
        parts.push(this.$tc("dmp.eventDefinition", 2));
      }
      if (this.dateRangeFilter) {
        parts.push(
          this.dateRangeFilter === 30
            ? this.$t("dmp.dateRangeLast30")
            : this.$t("dmp.dateRangeLast60")
        );
      } else {
        if (this.startDatetime) {
          parts.push(this.$t("alarm.start_date_filter"));
        }
        if (this.endDatetime){
          parts.push(this.$t("alarm.end_date_filter"));
        }
      }
      if (this.recoveredFilter !== null){
        parts.push(this.$tc("dmp.isRecovered", 2));
      }
      if (this.deviceIdFilter.trim()){
        parts.push(this.$t("dmp.deviceId"));
      }
      if (this.deviceNameFilter.trim()){
        parts.push(this.$t("dmp.deviceName"));
      }
      return parts.length ? parts.join("<br>") : '';
    },
  },
  methods: {
    async onShow() {
      await this.$store.dispatch("dmp_panels/getAll");
      await this.$store.dispatch("dmp_events_definition/getAll");

      if (this.filters && Object.keys(this.filters).length) {
        this.panelFilter = this.toArray(this.filters.panelFilter);
        this.autoDetectPanel = !!this.filters.autoDetectPanel;
        this.eventDefinitionOptionsFilter = this.filters.eventDefinitionOptionsFilter || [];
        this.startDatetime = this.filters.startDatetime || "";
        this.endDatetime = this.filters.endDatetime || "";
        this.dateRangeFilter = this.filters.dateRangeFilter || null;
        this.recoveredFilter = this.filters.recoveredFilter !== undefined ? this.filters.recoveredFilter : null;
        this.deviceIdFilter = this.toText(this.filters.device_id);
        this.deviceNameFilter = this.toText(this.filters.device_name);
      } else {
        this.panelFilter = [];
        this.autoDetectPanel = false;
        this.eventDefinitionOptionsFilter = [];
        this.startDatetime = "";
        this.endDatetime = "";
        this.dateRangeFilter = null;
        this.recoveredFilter = null;
        this.deviceIdFilter = "";
        this.deviceNameFilter = "";
      }
    },
    toText(value) {
      if (Array.isArray(value)) return value.join(", ");
      return value || "";
    },
    toArray(value) {
      if (Array.isArray(value)) return value;
      return value !== undefined && value !== null && value !== "" ? [value] : [];
    },
    // Only Accept emits "filters-applied"; the prop watcher re-emits
    // "filters-selected" on its own and must not sync the placed slots.
    applyFilters() {
      const payload = this.updateFilters();
      this.$emit("filters-applied", payload);
      this.$bvModal.hide("filter_modal");
    },
    updateFilters() {
      // device_id / device_name use the FacetedSearch query-parameter names.
      const filters = {
        panelFilter: this.panelFilter,
        autoDetectPanel: this.autoDetectPanel,
        eventDefinitionOptionsFilter: this.eventDefinitionOptionsFilter,
        startDatetime: this.startDatetime,
        endDatetime: this.endDatetime,
        dateRangeFilter: this.dateRangeFilter,
        recoveredFilter: this.recoveredFilter,
        device_id: this.deviceIdFilter.trim(),
        device_name: this.deviceNameFilter.trim(),
      };
      const payload = {
        filters,
        filtersText: this.filtersText,
        filtersCount: this.countActiveFilters(filters),
      };
      this.$emit("filters-selected", payload);
      return payload;
    },
    countActiveFilters(filters) {
      if (!filters) return 0;
      let count = 0;
      for (const key in filters) {
        const value = filters[key];
        if (key === "autoDetectPanel") {
          if (value) count++;
        } else if (Array.isArray(value)) {
          if (value.length > 0) count++;
        } else if (value !== null && value !== "" && value !== undefined) {
          count++;
        }
      }
      return count;
    },
  },
  setup() {
    return {
      withPopper: popperHelper.withPopper,
    };
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler(newFilters, oldFilters) {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          this.panelFilter = this.toArray(newFilters.panelFilter);
          this.autoDetectPanel = !!newFilters.autoDetectPanel;
          this.eventDefinitionOptionsFilter = newFilters.eventDefinitionOptionsFilter || [];
          this.startDatetime = newFilters.startDatetime || "";
          this.endDatetime = newFilters.endDatetime || "";
          this.dateRangeFilter = newFilters.dateRangeFilter || null;
          this.recoveredFilter = newFilters.recoveredFilter ?? null;
          this.deviceIdFilter = this.toText(newFilters.device_id);
          this.deviceNameFilter = this.toText(newFilters.device_name);
          this.updateFilters();
        }
      },
    },
  },
};
</script>

<style lang="scss">
.panel-option {
  display: flex;
  width: 100%;
  min-height: 28px;
  align-items: center;
  cursor: pointer;
}

.custom-option-checkbox {
  display: inline-flex;
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  align-items: center;
  justify-content: center;
  border: 1px solid #b9b9c3;
  border-radius: 3px;
  background-color: #fff;
  pointer-events: none;
}

.custom-option-checkbox-selected {
  color: #fff;
  border-color: #7367f0;
  background-color: #7367f0;
}

.vs__dropdown-option .panel-option {
  width: 100%;
}
</style>
